<template>
  <div class="team-effectiveness-bar-graph" v-if="doc">
    <div>
      <div style="height: 50px"></div>

      <div :class="row.id" :key="row.id" v-for="row in rows">
        <a
          href="javascript:void(0)"
          @click="click(row)"
          class="a-block leaderTypeFitGraphRowAnchor cs-accordion-anchor"
        >
          <div class="leaderTypeFitGraph row cs-accordion-anchor-child">
            <div
              class="leaderTypeFitGraphLabel"
              :class="'color-' + row.id.toLowerCase()"
              style="line-height: 1; display: flex; align-items: center"
            >
              <div>
                {{ row.shortTitle }}
              </div>
            </div>
            <div class="leaderTypeFitGraphArea">
              <div class="leaderTypeFitCutoff" style="right: 0%"></div>
              <div class="leaderTypeFitCutoff" style="right: 25%"></div>
              <div class="leaderTypeFitCutoff" style="left: 50%"></div>
              <div class="leaderTypeFitCutoff" style="left: 25%"></div>
              <div class="leaderTypeFitCutoff" style="left: 0%"></div>

              <Tooltip :text="tooltip(row)">
                <span
                  class="leaderTypeGraphBar"
                  :class="'btn-' + row.id.toLowerCase()"
                  :style="bar(row.min, row.max, 5)"
                ></span>
              </Tooltip>

              <Tooltip :text="tooltip(row)">
                <span
                  class="leaderTypeGraphBar"
                  :class="'btn-' + row.id.toLowerCase()"
                  :style="square(row.mean)"
                ></span>
              </Tooltip>
            </div>
          </div>
        </a>

        <div
          :class="{ hidden: row.id !== activeRowId }"
          class="leaderTypeFitGraphInfo cs-accordion-panel"
        >
          <hr />
          <div class="row">
            <p class="col-sm-7 text-xs-center text-sm-left">
              {{ row.content }}
            </p>
            <div class="col-sm-5 text-center">
              <p style="font-size: 20px">Mean Rating:</p>
              <p style="font-size: 50px">{{ row.mean.toFixed(1) }}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="leaderTypeFitGraphXAxis">
        <div class="leaderTypeFitGraphXAxisLabels">
          <div class="leaderTypeFitGraphXAxisLabel" style="left: 75%; width: 25%">
            Strongly <br />
            agree
          </div>
          <div class="leaderTypeFitGraphXAxisLabel" style="left: 50%; width: 25%">Agree</div>
          <div class="leaderTypeFitGraphXAxisLabel" style="left: 25%; width: 25%">Disagree</div>
          <div class="leaderTypeFitGraphXAxisLabel" style="left: 0%; width: 25%">
            Strongly <br />
            disagree
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { Tooltip } from "uiv";
import cms from "../../services/cms/cms";
import {
  CmsDocumentTypes,
  CmsTeamEffectivenessReview,
} from "../../services/cms/cms-type-definitions";
import cmsContentMap from "../../services/cms/cms-content-map";
import { TeamEffectivenessReviewApiModel } from "../../api-models";
import { getTeamEffectivenessReport } from "./get-team-effectiveness-report-team-id";

interface TebgRow {
  id: string;
  shortTitle: string;
  content: string;
  min: number;
  mean: number;
  q1: number;
  median: number;
  q3: number;
  max: number;
}

function acs(arr: number[]): number[] {
  return arr.sort((a, b) => a - b);
}

function quantile(arr: number[], q: number): number {
  const sorted = acs(arr);
  const pos = (sorted.length - 1) * q;
  const base = Math.floor(pos);
  const rest = pos - base;
  if (sorted[base + 1] !== undefined) {
    return sorted[base] + rest * (sorted[base + 1] - sorted[base]);
  }
  return sorted[base];
}

const props = defineProps<{
  reviewId: string | undefined;
}>();

const show = ref(true);

const doc = ref<CmsTeamEffectivenessReview | null>(null);

const ter = ref<TeamEffectivenessReviewApiModel | null>(null);

const activeRowId = ref("");

function score(id: string): TebgRow | null {
  if (!ter.value) {
    return null;
  }

  const rawRatings = ter.value.questionnaires.map((x) => x.rawRatings.ratings[id]);
  const max = Math.max(...rawRatings);
  const min = Math.min(...rawRatings);
  const q1 = quantile(rawRatings, 0.25);
  const median = quantile(rawRatings, 0.5);
  const q3 = quantile(rawRatings, 0.75);

  const team = ter.value.aggregateRatings.meanRatings[id];
  const slice = doc.value?.data.body.find((x) => x.primary.question_id === id);

  const row: TebgRow = {
    id,
    shortTitle: cms.asText(slice?.primary.question_short_title),
    content: cms.asText(slice?.primary.question_bar_chart_content),
    min,
    mean: team,
    q1,
    median,
    q3,
    max,
  };
  return row;
}

const rows = computed<(TebgRow | null)[]>(() => {
  if (!ter.value) {
    return [];
  }

  return [
    score("Professional"),
    score("Strategist"),
    score("Practical"),
    score("Servant"),
    score("Executional"),
    score("Transformational"),
    score("Entrepreneurial"),
    score("Charismatic"),
    score("Innovator"),
  ];
});

function click(row: TebgRow | null): void {
  activeRowId.value = row?.id === activeRowId.value ? "" : row?.id ?? "";
}

function bar(start: number, end: number, height: number): string {
  const startValue = start * 10;
  const endValue = end * 10;

  return `position: absolute;
    left: ${startValue}%;
    right: ${endValue}%;
    width: ${endValue - startValue}%;
    border-top-width: ${height}px;
    height: ${height}px;
    top: ${(50 - height) / 2}px;
    `;
}

function square(x: number): string {
  const scaledX = x * 10;
  const size = 40;

  return `position: absolute;
    left: calc(${scaledX}% - ${size / 2}px);
    width: ${size}px;
    background: white;
    border-width: 5px;
    height: ${size}px;
    top: 5px;
    `;
}

function tooltip(row: TebgRow): string {
  return `This team rated ‘${row.shortTitle}’ between ${row.min.toFixed(1)} and ${row.max.toFixed(
    1,
  )} with an average of ${row.mean.toFixed(1)}`;
}

function desc(id: string): string {
  return cms.asText(
    doc.value?.data.body.find((x) => x.primary.question_id.toLowerCase() === id.toLowerCase())
      ?.primary.question_title,
  );
}

onMounted(() => {
  cms
    .getById(
      CmsDocumentTypes.CmsTeamEffectivenessReview,
      cmsContentMap.teamEffectiveness.questionnaireQuestions,
    )
    .then((d) => {
      doc.value = d;
    });

  getTeamEffectivenessReport(props.reviewId).then((data) => {
    ter.value = data;

    // Set the highest rating as active.
    let lowestRating = -1;
    Object.entries(ter.value.aggregateRatings.meanRatings)
      .filter((r) => r[1] > lowestRating)
      .forEach((rating) => {
        [activeRowId.value, lowestRating] = rating;
      });
  });
});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="less">
@import (reference) "../../styles/bootstrap/variables.less";

.tooltip {
  z-index: 2147483646 !important;
}

.team-effectiveness-bar-graph {
  position: relative;
  padding-bottom: 20px;

  .ter-bottom-nav {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    background: gray;
  }

  .ter-row {
    position: relative;
    height: 100px;
  }

  .ter-self-box {
    height: 34px;
    width: 34px;
  }

  .ter-team-box {
    height: 34px;
    width: 34px;
    background-color: white !important;
    border: 2px solid;
  }

  .ter-range-bar {
    margin-top: 7px;
    height: 20px;
  }

  .scale {
    margin: 0 auto;
    margin-top: 5px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    padding-left: 30px;
    padding-right: 30px;

    & > span {
      display: block;
      width: 8em;
      text-align: center;

      &:first-child {
        text-align: left;
      }

      &:last-child {
        text-align: right;
      }
    }
  }

  .ter-innovator {
    .ter-colored {
      background-color: @innovator-color;
      border-color: @innovator-color;
    }
  }

  .ter-strategist {
    .ter-colored {
      background-color: @strategist-color;
      border-color: @strategist-color;
    }
  }

  .ter-transformational {
    .ter-colored {
      background-color: @transformational-color;
      border-color: @transformational-color;
    }
  }

  .ter-professional {
    .ter-colored {
      background-color: @professional-color;
      border-color: @professional-color;
    }
  }

  .ter-entrepreneurial {
    .ter-colored {
      background-color: @entrepreneurial-color;
      border-color: @entrepreneurial-color;
    }
  }

  .ter-charismatic {
    .ter-colored {
      background-color: @charismatic-color;
      border-color: @charismatic-color;
    }
  }

  .ter-servant {
    .ter-colored {
      background-color: @servant-color;
      border-color: @servant-color;
    }
  }

  .ter-practical {
    .ter-colored {
      background-color: @practical-color;
      border-color: @practical-color;
    }
  }

  .ter-executional {
    .ter-colored {
      background-color: @executional-color;
      border-color: @executional-color;
    }
  }
}
</style>
